/**
 * Stammdaten (Spec 8.1, 10.1).
 *
 * Der praktische Test aus Spec 10.9: Ein neuer Bereich mit eigenem Topf muss
 * sich vollständig hier einrichten lassen — Pool anlegen, Kassen zuordnen,
 * Arbeitsbereiche zuordnen. Ist dafür ein Deployment nötig, sitzt die
 * Abstraktion an der falschen Stelle.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PoolDto, type PoolGroupTemplateDto } from "../lib/api";
import { ART_LABEL, beschreibung } from "../lib/berechnung";
import { Empty, ErrorBox, Info, PageHeader, Section, Spinner, StatusChip } from "../components/ui";
import { Workstations } from "./masterData/Workstations";
import { Users } from "./masterData/Users";
import { Employees } from "./masterData/Employees";
import { useMe } from "./Anmeldung";
import { canWrite, isAdmin } from "../lib/auth";
import { FILTERS, type EmployeeFilter } from "../lib/employees";

type Tab = "employees" | "pools" | "workstations" | "areas" | "settings" | "users";

/** Reiter in der Adresse, damit andere Masken direkt dorthin verlinken können. */
const TAB_PARAM: Record<string, Tab> = { mitarbeiter: "employees", benutzer: "users" };

const TABS: [Tab, string][] = [
  ["pools", "Pools"],
  ["workstations", "Kassen"],
  ["areas", "Arbeitsbereiche"],
  ["settings", "Einstellungen"],
];

export function MasterData() {
  const [params] = useSearchParams();
  const me = useMe().data;
  // Mitarbeitende mit IBANs sieht nur, wer Bankdaten sehen darf.
  const showEmployees = canWrite(me?.user);
  // Zugänge verwaltet nur die Administration — auch mit „Mit Microsoft
  // anmelden": wer rein darf und mit welcher Rolle, entscheidet die App.
  const showUsers = isAdmin(me?.user);
  const tabs: [Tab, string][] = [
    ...(showEmployees ? ([["employees", "Mitarbeitende"]] as [Tab, string][]) : []),
    ...TABS,
    ...(showUsers ? ([["users", "Zugänge"]] as [Tab, string][]) : []),
  ];
  const fromUrl = TAB_PARAM[params.get("tab") ?? ""];
  const [tab, setTab] = useState<Tab>(fromUrl ?? (showEmployees ? "employees" : "pools"));
  const filterParam = params.get("filter");
  const initialFilter = FILTERS.some((f) => f.value === filterParam) ? (filterParam as EmployeeFilter) : undefined;

  return (
    <>
      <PageHeader label="Stammdaten" title="Mitarbeitende, Pools, Kassen, Bereiche">
        Alles, was sich ändern kann, ohne dass sich die Software ändert, wird hier gepflegt.
        Ein neuer Bereich mit eigenem Trinkgeldtopf braucht keine neue Softwareversion.
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-2">
        {tabs.map(([value, label]) => (
          <button
            key={value}
            className={tab === value ? "btn-primary" : "btn-ghost"}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "employees" && showEmployees ? (
        <Employees initialFilter={initialFilter} initialSearch={params.get("suche") ?? ""} />
      ) : null}
      {tab === "pools" ? <Pools /> : null}
      {tab === "workstations" ? <Workstations /> : null}
      {tab === "areas" ? <WorkAreas /> : null}
      {tab === "settings" ? <Settings /> : null}
      {tab === "users" && showUsers && me?.user ? <Users selfId={me.user.id} /> : null}
    </>
  );
}

function Pools() {
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const queryClient = useQueryClient();

  const pools = useQuery({
    queryKey: ["pools"],
    queryFn: () => api.get<{ pools: PoolDto[] }>("/pools"),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post("/pools", {
        code: code.toUpperCase(),
        name,
        sortOrder: (pools.data?.pools.length ?? 0) + 1,
        validFrom: validFrom || null,
        validTo: validTo || null,
      }),
    onSuccess: async () => {
      setAdding(false);
      setCode("");
      setName("");
      setValidFrom("");
      setValidTo("");
      await queryClient.invalidateQueries();
    },
  });

  const toggle = useMutation({
    mutationFn: (pool: PoolDto) => api.patch(`/pools/${pool.id}`, { active: !pool.active }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (pools.isLoading) return <Spinner />;

  return (
    <Section
      label="Trinkgeldtöpfe"
      title="Pools"
      actions={
        <button className="btn-primary" onClick={() => setAdding((v) => !v)}>
          {adding ? "Abbrechen" : "Pool anlegen"}
        </button>
      }
    >
      {adding ? (
        <div className="card mb-6 p-5">
          <p className="mb-4 text-xs text-muted">
            Ohne zugeordnete Kassen und Arbeitsbereiche bleibt ein Pool wirkungslos. Das ist
            gewollt: ein halb konfigurierter Pool darf nichts kaputtmachen.
          </p>
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="block">
              <span className="label mb-1 block">Kürzel</span>
              <input
                className="field font-mono uppercase"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="WINTER_WALK"
              />
            </label>
            <label className="block">
              <span className="label mb-1 block">Name</span>
              <input
                className="field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Winter Walk"
              />
            </label>
            <label className="block">
              <span className="label mb-1 block">gültig ab</span>
              <input
                type="date"
                className="field"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="label mb-1 block">gültig bis</span>
              <input
                type="date"
                className="field"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
              />
            </label>
          </div>
          {create.error ? (
            <div className="mt-4">
              <ErrorBox error={create.error} />
            </div>
          ) : null}
          <button
            className="btn-primary mt-4"
            onClick={() => create.mutate()}
            disabled={!code || !name || create.isPending}
          >
            Anlegen
          </button>
        </div>
      ) : null}

      <div className="card overflow-hidden">
        <table className="tbl">
          <thead>
            <tr>
              <th className="w-40">
                Kürzel
                <Info text="Nach der Anlage unveränderlich. Es steht in Exporten und in jedem Berechnungslauf, deshalb darf es sich nie ändern." />
              </th>
              <th>Name</th>
              <th className="w-56">
                Saison
                <Info text="Außerhalb der Gültigkeit fehlt der Pool in der Berechnung des Tages und wird bei der Partitionsprüfung übergangen. Historische Tage bleiben unberührt." />
              </th>
              <th className="w-24">Status</th>
              <th className="w-28"></th>
            </tr>
          </thead>
          <tbody>
            {pools.data?.pools.map((p) => (
              <tr key={p.id}>
                <td className="font-mono text-xs">{p.code}</td>
                <td>{p.name}</td>
                <td className="text-muted">
                  {p.validFrom || p.validTo
                    ? `${p.validFrom ?? "immer"} bis ${p.validTo ?? "offen"}`
                    : "ganzjährig"}
                </td>
                <td>
                  <StatusChip status={p.active ? "ACTIVE" : "INACTIVE"} />
                </td>
                <td>
                  <button className="btn-ghost" onClick={() => toggle.mutate(p)}>
                    {p.active ? "Deaktivieren" : "Aktivieren"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted">
        Pools werden nie gelöscht, nur deaktiviert. Sonst wären abgeschlossene Auszahlungen
        nicht mehr nachvollziehbar. Das Kürzel ist nach der Anlage unveränderlich.
      </p>
    </Section>
  );
}

interface WorkAreaDto {
  id: string;
  name: string;
  poolId: string | null;
  poolCode: string | null;
  tipEligible: boolean;
  active: boolean;
}

function WorkAreas() {
  const queryClient = useQueryClient();
  // Eine Zuordnung zu ändern verschiebt Geld zwischen Töpfen. Deshalb wird sie
  // erst vorgemerkt und braucht einen zweiten, bewussten Klick.
  const [staged, setStaged] = useState<Record<string, string>>({});
  const areas = useQuery({
    queryKey: ["work-areas"],
    queryFn: () => api.get<{ workAreas: WorkAreaDto[] }>("/work-areas"),
  });
  const pools = useQuery({
    queryKey: ["pools"],
    queryFn: () => api.get<{ pools: PoolDto[] }>("/pools"),
  });

  const setPool = useMutation({
    mutationFn: ({ id, poolId }: { id: string; poolId: string | null }) =>
      api.patch(`/work-areas/${id}`, { poolId }),
    onSuccess: async (_data, variables) => {
      setStaged((prev) => {
        const next = { ...prev };
        delete next[variables.id];
        return next;
      });
      await queryClient.invalidateQueries();
    },
  });

  if (areas.isLoading) return <Spinner />;

  const eligible = areas.data?.workAreas.filter((a) => a.tipEligible) ?? [];
  const notEligible = areas.data?.workAreas.filter((a) => !a.tipEligible) ?? [];

  const tableProps = {
    pools: pools.data?.pools ?? [],
    staged,
    setStaged,
    onSave: setPool.mutate,
    pending: setPool.isPending,
  };

  return (
    <>
      <Section label="Trinkgeldberechtigt" title="Bereiche mit Topf">
        <AreaTable areas={eligible} {...tableProps} />
      </Section>
      <Section label="Ohne Anspruch" title="Nicht trinkgeldberechtigt">
        <AreaTable areas={notEligible} {...tableProps} />
        <p className="mt-3 text-xs text-muted">
          GASTRO ist in Gastromatic der Platzhalter an Tagen ohne Schicht, `-` steht für gar
          keinen Bereich. Beide dürfen nie in die Verteilung.
        </p>
      </Section>
      {setPool.error ? <ErrorBox error={setPool.error} /> : null}
    </>
  );
}

function AreaTable({
  areas,
  pools,
  staged,
  setStaged,
  onSave,
  pending,
}: {
  areas: WorkAreaDto[];
  pools: PoolDto[];
  staged: Record<string, string>;
  setStaged: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  onSave: (args: { id: string; poolId: string | null }) => void;
  pending: boolean;
}) {
  if (areas.length === 0) return <Empty>Keine Bereiche in dieser Gruppe.</Empty>;
  return (
    <div className="card overflow-hidden">
      <table className="tbl">
        <thead>
          <tr>
            <th>
              Arbeitsbereich
              <Info text="Muss exakt der Spalte Arbeitsbereich im Gastromatic-Export entsprechen." />
            </th>
            <th className="w-56">
              Pool
              <Info text="Bestimmt, aus welchem Topf die Stunden dieses Bereichs bedient werden. Ohne Pool ist der Bereich nicht trinkgeldberechtigt." />
            </th>
            <th className="w-40"></th>
          </tr>
        </thead>
        <tbody>
          {areas.map((a) => {
            const current = a.poolId ?? "";
            const value = staged[a.id] ?? current;
            const dirty = value !== current;
            return (
              <tr key={a.id} className={dirty ? "bg-warn/5" : ""}>
                <td>{a.name}</td>
                <td>
                  <select
                    className="field py-1 text-xs"
                    value={value}
                    onChange={(e) =>
                      setStaged((prev) => ({ ...prev, [a.id]: e.target.value }))
                    }
                  >
                    <option value="">– nicht berechtigt –</option>
                    {pools
                      .filter((p) => p.active)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                </td>
                <td>
                  {dirty ? (
                    <>
                      <button
                        className="btn-primary"
                        disabled={pending}
                        onClick={() => onSave({ id: a.id, poolId: value || null })}
                        title="Ändert, aus welchem Topf dieser Bereich bedient wird. Wird protokolliert."
                      >
                        Speichern
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() =>
                          setStaged((prev) => {
                            const next = { ...prev };
                            delete next[a.id];
                            return next;
                          })
                        }
                      >
                        Zurück
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface SettingDto {
  key: string;
  value: unknown;
  description: string | null;
  updatedAt: string;
}

function Settings() {
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<{ settings: SettingDto[] }>("/settings"),
  });
  const queryClient = useQueryClient();
  const [edited, setEdited] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => {
      // Zahlen als Zahl speichern, alles andere als String.
      const parsed = /^-?\d+(\.\d+)?$/.test(value) ? Number(value) : value;
      return api.patch(`/settings/${key}`, { value: parsed });
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (settings.isLoading) return <Spinner />;

  return (
    <>
    <VorgabeBerechnung />
    <Section label="Parameter" title="Einstellungen">
      <div className="card overflow-hidden">
        <table className="tbl">
          <thead>
            <tr>
              <th className="w-72">Schlüssel</th>
              <th className="w-40">
                Wert
                <Info text="Änderungen wirken nur auf künftige Abgleiche und Berechnungen. Bereits bestätigte Sessions und abgeschlossene Läufe bleiben, wie sie sind." />
              </th>
              <th>Bedeutung</th>
              <th className="w-28"></th>
            </tr>
          </thead>
          <tbody>
            {settings.data?.settings.map((s) => {
              const current = String(s.value);
              const value = edited[s.key] ?? current;
              const dirty = value !== current;
              return (
                <tr key={s.key}>
                  <td className="font-mono text-xs">{s.key}</td>
                  <td>
                    <input
                      className="field py-1 font-mono text-xs"
                      value={value}
                      onChange={(e) =>
                        setEdited((prev) => ({ ...prev, [s.key]: e.target.value }))
                      }
                    />
                  </td>
                  <td className="text-xs text-muted">{s.description}</td>
                  <td>
                    {dirty ? (
                      <button
                        className="btn-primary"
                        onClick={() => save.mutate({ key: s.key, value })}
                      >
                        Speichern
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {save.error ? (
        <div className="mt-4">
          <ErrorBox error={save.error} />
        </div>
      ) : null}
      <p className="mt-3 text-xs text-muted">
        Änderungen wirken nur auf künftige Abgleiche und Berechnungen. Bestätigte Sessions und
        abgeschlossene Läufe bleiben unverändert. Jede Änderung wird protokolliert.
      </p>
    </Section>
    </>
  );
}

/**
 * Wie neue Tage gerechnet werden (Vorlage mit `isDefault`). Verschiebt Geld
 * zwischen Menschen, deshalb wie alle Stammdaten: erst wählen, dann bewusst
 * übernehmen. Schon gerechnete Tage behalten ihre Aufteilung; einzeln
 * umgestellt wird am Betriebstag.
 */
function VorgabeBerechnung() {
  const queryClient = useQueryClient();
  const templates = useQuery({
    queryKey: ["pool-group-templates"],
    queryFn: () => api.get<{ templates: PoolGroupTemplateDto[] }>("/pool-group-templates"),
  });
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const speichern = useMutation({
    mutationFn: (templateId: string) => api.put("/pool-group-templates/vorgabe", { templateId }),
    onSuccess: async () => {
      setGewaehlt(null);
      await queryClient.invalidateQueries();
    },
  });

  const liste = templates.data?.templates ?? [];
  const aktuell = liste.find((t) => t.isDefault)?.id ?? "";
  const wert = gewaehlt ?? aktuell;
  if (liste.length === 0) return null;

  return (
    <Section label="Berechnung" title="Vorgabe für neue Tage">
      <div className="card p-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="field w-auto"
            value={wert}
            onChange={(e) => setGewaehlt(e.target.value)}
            aria-label="Vorgabe für neue Tage"
          >
            {liste.map((t) => (
              <option key={t.id} value={t.id}>
                {ART_LABEL[t.art]}
                {t.art === "teil" ? `: ${beschreibung(t.gruppen)}` : ""}
              </option>
            ))}
          </select>
          {wert !== aktuell ? (
            <>
              <button className="btn-primary" disabled={speichern.isPending} onClick={() => speichern.mutate(wert)}>
                {speichern.isPending ? "Speichert" : "Als Vorgabe übernehmen"}
              </button>
              <button className="btn-ghost" onClick={() => setGewaehlt(null)}>
                Verwerfen
              </button>
            </>
          ) : null}
        </div>
        <p className="mt-2 text-xs text-muted">
          Gilt für Tage, die noch nicht gerechnet sind. Schon gerechnete behalten ihre Aufteilung;
          einzeln umstellen geht am Betriebstag unter „Berechnung“.
        </p>
        {speichern.error ? (
          <div className="mt-2">
            <ErrorBox error={speichern.error} />
          </div>
        ) : null}
      </div>
    </Section>
  );
}
